import React, { useState } from 'react';
import { auth } from '../api';
import '../styles/PasswordReset.css';

function PasswordReset({ onBackToLogin, prefilledToken = '' }) {
  const [step, setStep] = useState(prefilledToken ? 'reset' : 'request'); // 'request' | 'reset' | 'success'
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState(prefilledToken);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Step 1: Request password reset
  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await auth.requestPasswordReset({ email });

      if (response.status === 200) {
        setSuccessMessage('✅ Check your email for password reset instructions');
        setStep('reset');
        setEmail('');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Reset password with token
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await auth.resetPassword({
        token: resetToken,
        newPassword: newPassword
      });

      if (response.status === 200) {
        setStep('success');
        setResetToken('');
        setNewPassword('');
        setConfirmPassword('');

        // Auto-redirect after 3 seconds
        setTimeout(onBackToLogin, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password. Token may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="password-reset-container">
      <div className="password-reset-card">
        <h2>🔐 Reset Password</h2>

        {/* Step 1: Request Reset */}
        {step === 'request' && (
          <form onSubmit={handleRequestReset}>
            <p className="help-text">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <button type="submit" disabled={loading}>
              {loading ? '⏳ Sending...' : '📧 Send Reset Link'}
            </button>

            <button
              type="button"
              className="back-button"
              onClick={onBackToLogin}
            >
              ← Back to Login
            </button>
          </form>
        )}

        {/* Step 2: Reset with Token */}
        {step === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <p className="help-text">
              Enter the reset token from your email and your new password.
            </p>

            <input
              type="text"
              placeholder="Reset token from email"
              value={resetToken}
              onChange={(e) => setResetToken(e.target.value)}
              required
              disabled={loading}
            />

            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={loading}
            />

            <input
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
            />

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading}>
              {loading ? '⏳ Resetting...' : '🔄 Reset Password'}
            </button>

            <button
              type="button"
              className="back-button"
              onClick={onBackToLogin}
            >
              ← Back to Login
            </button>
          </form>
        )}

        {/* Step 3: Success */}
        {step === 'success' && (
          <div className="success-container">
            <div className="success-icon">✅</div>
            <h3>Password Reset Successfully!</h3>
            <p>Your password has been reset. Redirecting to login...</p>
            <button onClick={onBackToLogin} className="primary-button">
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PasswordReset;
